/**
 * CashmereService — Cashmere Boutique via CJ Affiliate
 * Advertiser: 1989180 | Publisher: 101729219
 * EPC Evergreen: $72.26 — top performer
 *
 * ⚡ Scraping LIVE du site cashmereboutique.com (nouveaux produits apparaissent automatiquement)
 * Cache 6h dans AsyncStorage. Fallback sur liste statique si le scraping échoue.
 */
import { Linking } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

const PUB  = '101729219';
const BASE = 'https://www.cashmereboutique.com';
// CDN RocketCDN utilisé par le site — sert les images sans hotlink protection
const CDN  = 'https://876572ee.delivery.rocketcdn.me/wp-content/uploads';

// Cache 6h
const CACHE_KEY = 'cashmere_live_v4'; // v4: fallback + live = wsrv.nl wrap
const CACHE_TTL = 6 * 60 * 60 * 1000;
const USD_TO_EUR = 0.92;

// Catégories du site avec genre et mapping
const CATEGORIES = [
  { path: 'women/sweaters',      gender: 'F', cat: 'Hauts' },
  { path: 'women/robes',         gender: 'F', cat: 'Robes' },
  { path: 'women/coats',         gender: 'F', cat: 'Vestes' },
  { path: 'women/dresses',       gender: 'F', cat: 'Robes' },
  { path: 'women/pashminas',     gender: 'F', cat: 'Accessoires' },
  { path: 'men/sweaters-men',    gender: 'H', cat: 'Hauts' },
  { path: 'men/robes-men',       gender: 'H', cat: 'Robes' },
  { path: 'men/coats-men',       gender: 'H', cat: 'Vestes' },
  { path: 'scarves',             gender: '*', cat: 'Accessoires' },
  { path: 'accessories',         gender: '*', cat: 'Accessoires' },
];

export function getCBEvergreen(path, userId) {
  const url = path ? `${BASE}${path}` : BASE;
  const sid = userId ? `&sid=${userId}` : '';
  return `https://www.anrdoezrs.net/click-${PUB}-15733331?url=${encodeURIComponent(url)}${sid}`;
}

export function getCBPromo50(path) {
  // Lien CJ officiel — compte les clics dans le dashboard CJ publisher 101729219
  const base = `https://www.kqzyfj.com/click-${PUB}-10471851`;
  if (!path || path === '/') return base;
  const dest = path.startsWith('http') ? path : `${BASE}${path}`;
  return `${base}?url=${encodeURIComponent(dest)}`;
}

// Lien Evergreen CJ (EPC $72.26)
export function getCBEvergreenUrl(path) {
  const base = `https://www.anrdoezrs.net/click-${PUB}-15733331`;
  if (!path || path === '/') return base;
  const dest = path.startsWith('http') ? path : `${BASE}${path}`;
  return `${base}?url=${encodeURIComponent(dest)}`;
}

export function pingCBImpression() {
  fetch(`https://www.lduhtrp.net/image-${PUB}-10471851`, { method: 'GET', mode: 'no-cors' }).catch(() => {});
}

// ═══════════════════════════════════════════════════════════════
// LIVE SCRAPING — récupère les produits à jour depuis le site
// ═══════════════════════════════════════════════════════════════

/**
 * Parse une page HTML de catégorie et extrait les produits
 * Format HTML typique : 
 *   ![NAME](IMAGE_URL) ... [Quickview](PRODUCT_URL) ... Original price was: $ORIG ... $PRICE Current price
 */
function parseCategoryHtml(html, gender, cat) {
  const products = [];
  const seen = new Set();
  
  // Regex capturant: image, nom (alt), URL produit, prix original, prix actuel
  // On cherche des patterns en "Sale!\n ![NAME](IMAGE) [Quickview](URL) ... $ORIG ... $PRICE"
  const re = /!\[([^\]]{5,150})\]\((https?:\/\/[^)]+?\.(?:webp|jpg|jpeg|png))[^)]*\)[\s\S]{0,500}?\[Quickview\]\((https?:\/\/[^)]+?)\)[\s\S]{0,600}?Original price was:[\s\S]{0,20}\$([\d,]+(?:\.\d+)?)[\s\S]{0,50}?\$([\d,]+(?:\.\d+)?)Current price/g;

  let m;
  while ((m = re.exec(html)) !== null) {
    const [, name, image, url, origStr, priceStr] = m;
    if (seen.has(url)) continue;
    if (!name || name.toLowerCase().includes('swatch')) continue;
    if (name.length < 5) continue;
    
    const priceUsd = parseFloat(priceStr.replace(/,/g, ''));
    const origUsd  = parseFloat(origStr.replace(/,/g, ''));
    if (!priceUsd || !origUsd || priceUsd > origUsd) continue;
    
    seen.add(url);
    products.push({
      name: name.replace(/&#8217;|’/g, "'").replace(/&amp;/g, '&').trim(),
      image,
      productUrl: url,
      priceUsd,
      origPriceUsd: origUsd,
      gender,
      cat,
    });
  }
  return products;
}

/**
 * Scrape une catégorie (avec pagination auto jusqu'à 3 pages)
 */
async function scrapeCategory(categorySlug, gender, cat, maxPages = 3) {
  const allProducts = [];
  for (let page = 1; page <= maxPages; page++) {
    const url = page === 1
      ? `${BASE}/product-category/${categorySlug}/`
      : `${BASE}/product-category/${categorySlug}/page/${page}/`;
    try {
      const res = await fetch(url, {
        headers: {
          'User-Agent': 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X)',
          'Accept': 'text/html',
        },
      });
      if (!res.ok) break;
      const html = await res.text();
      const products = parseCategoryHtml(html, gender, cat);
      if (products.length === 0) break; // pas de produits = fin de pagination
      allProducts.push(...products);
      // Détecter si plus de pages (présence de lien "→")
      if (!html.includes(`/page/${page + 1}/`)) break;
    } catch (e) {
      console.warn(`[Cashmere] scrape ${categorySlug} p${page}:`, e.message);
      break;
    }
  }
  return allProducts;
}

/**
 * Scrape toutes les catégories en parallèle
 */
async function scrapeAllLive() {
  const results = await Promise.allSettled(
    CATEGORIES.map(c => scrapeCategory(c.path, c.gender, c.cat))
  );
  
  const all = [];
  const seen = new Set();
  for (const r of results) {
    if (r.status !== 'fulfilled') continue;
    for (const item of r.value) {
      if (seen.has(item.productUrl)) continue;
      seen.add(item.productUrl);
      all.push(item);
    }
  }
  
  // Transformer au format produit outfit
  return all.map((item, idx) => {
    const slug = item.productUrl.replace(`${BASE}/shop/`, '').replace(/\/$/, '');
    const price = Math.round(item.priceUsd * USD_TO_EUR);
    const orig  = Math.round(item.origPriceUsd * USD_TO_EUR);
    const discount = Math.round((1 - price / orig) * 100);
    // Réécrire image vers RocketCDN pour bypass hotlink
    let image = item.image.replace('www.cashmereboutique.com', '876572ee.delivery.rocketcdn.me');
    // Sécurité : wrapper via wsrv.nl (plus fiable universellement en RN)
    if (image && image.startsWith('http') && !image.includes('wsrv.nl')) {
      image = 'https://wsrv.nl/?url=' + encodeURIComponent(image) + '&w=600';
    }
    
    return {
      id: `cb_live_${idx}`,
      partner: 'cashmere',
      brand: 'Cashmere Boutique',
      gender: item.gender,
      name: item.name,
      category: item.cat,
      price,
      originalPrice: orig,
      priceUsd: item.priceUsd,
      origPriceUsd: item.origPriceUsd,
      discount: discount > 0 ? discount : 0,
      promoBadge: discount > 0 ? `-${discount}%` : '',
      emoji: item.cat === 'Hauts' ? '🧥' : item.cat === 'Vestes' ? '🧥' : item.cat === 'Robes' ? '👗' : item.cat === 'Bas' ? '👖' : '🧣',
      color: 'Cashmere',
      image,
      url: getCBPromo50(`/shop/${slug}/`),
      originalUrl: getCBPromo50(`/shop/${slug}/`),
      available: true,
      rating: 4.8,
      deliveryDays: '5-10 j',
      currency: 'EUR',
      sizes: item.cat === 'Hauts' || item.cat === 'Vestes' || item.cat === 'Robes' || item.cat === 'Bas' ? ['XS', 'S', 'M', 'L', 'XL'] : ['Unique'],
    };
  });
}

/**
 * Fonction principale — essaie le live scraping puis fallback statique
 */
export async function fetchLiveCashmereProducts() {
  // ═══ DÉSACTIVÉ : mode iQueens uniquement ═══
  return [];
}

export function getCashmereProducts(gender, category) {
  // ═══ DÉSACTIVÉ : mode iQueens uniquement ═══
  return new Promise(resolve => resolve([]));
}

export function getFallbackCashmereProducts(gender, category) {
  // ═══ DÉSACTIVÉ : mode iQueens uniquement ═══
  return [];
}

export async function openCBLink(url) {
  try { await Linking.openURL(url || getCBPromo50()); }
  catch (e) { console.warn('[CASHMERE]', e.message); }
}

// ─── Helper raccourci ─────────────────────────────────────────
// p(id, gender, category, name, price, origPrice, imagePath, urlSlug, color, sizes, emoji)
// Note: prix fournis en USD (prix affichés sur cashmereboutique.com), converti en EUR
// (USD_TO_EUR déjà déclaré en haut du fichier)
const toEur = (usd) => Math.round(usd * USD_TO_EUR);

const p = (id, gender, cat, name, priceUsd, origUsd, image, slug, color, sizes, emoji) => {
  const price = toEur(priceUsd);
  const orig  = toEur(origUsd);
  // URL image construite via RocketCDN puis wrappée par wsrv.nl (même stratégie que iQueens/live)
  const rawImg = `${CDN}/${image}`;
  const wrappedImg = 'https://wsrv.nl/?url=' + encodeURIComponent(rawImg) + '&w=600&output=webp';
  return ({
    id: `cb_${id}`,
    partner: 'cashmere',
    brand: 'Cashmere Boutique',
    gender,
    name,
    category: cat,
    price,
    originalPrice: orig,
    priceUsd,        // prix original USD (référence)
    origPriceUsd: origUsd,
    discount: Math.round((1 - price / orig) * 100),
    promoBadge: `-${Math.round((1 - price / orig) * 100)}%`,
    emoji: emoji || (cat === 'Hauts' ? '🧥' : cat === 'Vestes' ? '🧥' : cat === 'Robes' ? '👗' : cat === 'Bas' ? '👖' : cat === 'Accessoires' ? '🧣' : cat === 'Ensembles' ? '✨' : '✨'),
    color: color || 'Beige',
    image: wrappedImg,
    url: getCBPromo50(`/shop/${slug}/`),
    originalUrl: getCBPromo50(`/shop/${slug}/`),
    affiliateUrl: getCBPromo50(`/shop/${slug}/`),  // même chose que url — pour compatibilité filtres
    available: true,
    rating: 4.8,
    deliveryDays: '5-10 j',     // international US→FR
    currency: 'EUR',
    sizes: sizes || (cat === 'Hauts' || cat === 'Vestes' || cat === 'Robes' || cat === 'Bas' ? ['XS', 'S', 'M', 'L', 'XL'] : ['Unique']),
  });
};

// ═══════════════════════════════════════════════════════════════
// PRODUITS ACTUALISÉS 2026 — scraping direct cashmereboutique.com
// URLs vérifiées, images webp 2023-2025 qui existent
// ═══════════════════════════════════════════════════════════════
const ALL_PRODUCTS = [

  // ─── WOMEN'S SWEATERS (18 produits) ───
  p('w_sw_001', 'F', 'Hauts', "Women's Cashmere V-Neck Sweater", 119, 239, '2023/11/CSW9008_h-scaled-300x450.webp', 'women/sweaters/womens-cashmere-v-neck-sweater', 'Camel'),
  p('w_sw_002', 'F', 'Hauts', "Women's Cashmere Cardigan Twin Set", 165, 399, '2023/11/CSW9002_j-scaled-300x450.webp', 'gift-ideas/gifts-below-200/womens-cashmere-cardigan-twin-sweater-set', 'Noir'),
  p('w_sw_003', 'F', 'Hauts', "Women's Pure Cashmere Turtle Neck Sweater", 129, 259, '2023/07/csw9004_resized-scaled-300x450.webp', 'women/sweaters/womens-pure-cashmere-turtle-neck-sweater', 'Noir'),
  p('w_sw_004', 'F', 'Hauts', "Women's Pure Cashmere V-Neck Boyfriend Sweater", 129, 259, '2023/11/CSW9011_f-scaled-300x450.webp', 'gift-ideas/corporate-gift-ideas/womens-pure-cashmere-v-neck-boyfriend-sweater', 'Charcoal'),
  p('w_sw_005', 'F', 'Hauts', "Women's Crew Neck Cashmere Sweater", 119, 239, '2023/07/csw9003b_resized-scaled-300x450.webp', 'women/sweaters/womens-crew-neck-cashmere-sweater', 'Noir'),
  p('w_sw_006', 'F', 'Hauts', "Women's Cashmere Round Neck Cardigan", 139, 259, '2023/07/csw9007a_resized-scaled-300x450.webp', 'gift-ideas/gifts-below-150/womens-cashmere-round-neck-cardigan', 'Camel'),
  p('w_sw_007', 'F', 'Hauts', "Pure Cashmere Crew Neck Spring Sweater", 99, 199, '2023/11/CSW9021_b-scaled-300x450.webp', 'gift-ideas/gifts-below-100/pure-cashmere-crew-neck-spring-sweater-for-women', 'Burnt Orange'),
  p('w_sw_008', 'F', 'Hauts', "Women's Ruffled Long Front Open Cardigan", 179, 359, '2023/11/CSW9010_c-scaled-300x450.webp', 'women/sweaters/womens-falbala-pure-cashmere-sweater', 'Noir'),
  p('w_sw_009', 'F', 'Vestes', "Cashmere Cardigan with Fur Collar", 299, 499, '2025/04/CCWF1-300x449.webp', 'women/sweaters/cashmere-cardigan-with-fur-collar', 'Beige'),
  p('w_sw_010', 'F', 'Vestes', "Women's Cashmere Cape with Fox Fur", 299, 599, '2023/06/ccw01d-scaled-300x450.jpg', 'women/pashminas/fur-and-fur-pashminas/womens-cashmere-cape-with-fox-fur', 'Gris'),
  p('w_sw_011', 'F', 'Accessoires', "Pure Cashmere Shawl with Sleeves", 199, 399, '2023/11/SWS_a-scaled-300x450.webp', 'women/pashminas/pure-cashmere-pashminas/pure-cashmere-shawl-with-sleeves', 'Noir'),
  p('w_sw_012', 'F', 'Hauts', "Pure Cashmere Shrug Style Cardigan", 99, 199, '2023/11/WCSH_a-scaled-300x450.webp', 'women/sweaters/pure-cashmere-shrug-style-sweater', 'Noir'),
  p('w_sw_013', 'F', 'Hauts', "Pure Cashmere V-Neck Spring Sweater", 99, 199, '2023/11/CSW9022_a-scaled-300x450.webp', 'gift-ideas/gifts-below-100/pure-cashmere-v-neck-spring-sweater-for-women', 'Camel'),
  p('w_sw_014', 'F', 'Hauts', "100% Cashmere Cardigan with Open Front", 169, 329, '2023/07/CSW9025a_resized-scaled-300x450.webp', 'women/sweaters/lightweight-open-front-100-cashmere-cardigan-sweater', 'Beige'),
  p('w_sw_015', 'F', 'Hauts', "Women's Pure Cashmere Layering Sweater", 129, 259, '2023/11/CSW9009_d-scaled-300x450.webp', 'women/sweaters/womens-tank-top-in-100-cashmere', 'Noir'),
  p('w_sw_016', 'F', 'Hauts', "High Low Tank Top in Pure Cashmere", 99, 199, '2023/11/CSW9006_a-scaled-300x450.webp', 'women/sweaters/womens-tank-top-in-pure-cashmere', 'Vanille'),
  p('w_sw_017', 'F', 'Hauts', "Women's Pure Cashmere Front-Tail Open Cardigan", 99, 199, '2023/11/CSW9024_a-scaled-300x450.webp', 'women/sweaters/womens-pure-cashmere-front-tail-open-cardigan', 'Noir'),
  p('w_sw_018', 'F', 'Hauts', "Women's Lightweight V-Neck Sweater", 129, 259, '2023/11/CSW9012_c-scaled-300x450.webp', 'women/sweaters/womens-lightweight-v-neck-sweater-in-pure-cashmere', 'Mustard'),

  // ─── WOMEN'S ROBES (3 produits) ───
  p('w_rb_001', 'F', 'Robes', "Pure Cashmere Robe for Women", 399, 799, '2023/11/CWR_b-scaled-300x450.webp', 'women/robes/pure-cashmere-robe-for-women', 'Noir'),
  p('w_rb_002', 'F', 'Ensembles', "Pure Cashmere Lounge and Pajama Set", 329, 659, '2023/11/CPS_c-scaled-300x450.webp', 'women/robes/pure-cashmere-lounge-and-pajama-set-for-women', 'Noir'),
  p('w_rb_003', 'F', 'Robes', "Women's Short Robe in Pure Cashmere", 379, 759, '2023/11/CWRS_e-scaled-300x450.webp', 'women/robes/womens-short-robe-in-pure-cashmere', 'Noir'),

  // ─── WOMEN'S DRESSES / TROUSERS (2 produits) ───
  p('w_dr_001', 'F', 'Robes', "Pure Cashmere Dress", 179, 359, '2023/11/CWD1_a-scaled-300x450.webp', 'gift-ideas/gifts-below-150/pure-cashmere-dress', 'Noir'),
  p('w_dr_002', '*', 'Bas', "Unisex Trousers in Pure Cashmere", 179, 359, '2023/11/CUT_c-scaled-300x450.webp', 'women/dresses/unisex-trousers-in-pure-cashmere', 'Noir'),

  // ─── WOMEN'S COATS (7 produits) ───
  p('w_co_001', 'F', 'Vestes', "Women's Knee Length Overcoat", 699, 1399, '2023/11/OW82_k-scaled-300x450.webp', 'women/coats/womens-knee-length-overcoat-in-pure-cashmere', 'Noir'),
  p('w_co_002', 'F', 'Vestes', "Women's Full Length Overcoat", 749, 1499, '2023/07/ow80b_resized-scaled-300x450.webp', 'women/coats/womens-full-length-overcoat-in-pure-cashmere', 'Noir'),
  p('w_co_003', 'F', 'Vestes', "Women's Pure Cashmere Pea Coat", 599, 1199, '2023/11/WPC_c-scaled-300x450.webp', 'women/coats/womens-pure-cashmere-pea-coat', 'Charcoal'),
  p('w_co_004', 'F', 'Vestes', "Full Length Belted Coat in Pure Cashmere", 749, 1499, '2023/11/OW81_k-scaled-300x450.webp', 'women/coats/full-length-belted-coat-in-pure-cashmere', 'Noir'),
  p('w_co_005', 'F', 'Vestes', "Short Funnel Coat in Pure Cashmere", 549, 1099, '2023/07/ow84b_resized-scaled-300x450.webp', 'women/coats/short-funnel-coat-in-pure-cashmere', 'Navy'),
  p('w_co_006', 'F', 'Vestes', "Pure Cashmere Cape Style Coat", 649, 900, '2023/11/OW85_d-scaled-300x450.webp', 'women/coats/pure-cashmere-cape-style-coat', 'Charcoal'),
  p('w_co_007', 'F', 'Vestes', "Pure Cashmere Hooded Coat for Women", 549, 1099, '2023/07/ow88e_resized-scaled-300x450.webp', 'women/coats/pure-cashmere-hooded-coat-for-women', 'Beige'),

  // ─── WOMEN'S PASHMINAS / SHAWLS (19 produits) ───
  p('w_ps_001', 'F', 'Accessoires', "Pure Cashmere Shawl", 110, 278, '2023/11/3000_h-scaled-300x450.webp', 'women/pashminas/pure-cashmere-pashminas/pure-cashmere-shawl', 'Noir'),
  p('w_ps_002', 'F', 'Accessoires', "Pashmina and Silk Wrap", 109, 219, '2023/11/2000_a-scaled-300x450.webp', 'women/pashminas/silk-cashmere-pashminas/pashmina-and-silk-wrap', 'Angora'),
  p('w_ps_003', 'F', 'Accessoires', "Cashmere Wrap Swarovski Galaxy", 169, 339, '2023/06/7061a-scaled-300x450.jpg', 'women/pashminas/swarovski-pashminas/pashmina-wrap-with-swarovski-crystals-galaxy', 'Noir'),
  p('w_ps_004', 'F', 'Accessoires', "Cashmere Wrap Swarovski Palm Tree", 179, 259, '2021/12/7064_resized-300x360.webp', 'women/pashminas/swarovski-pashminas/pashmina-wrap-with-swarovski-crystals-in-palm-tree-design', 'Multicolore'),
  p('w_ps_005', 'F', 'Accessoires', "Cashmere Wrap in Tiger Print", 89, 179, '2023/06/7052b-scaled-300x450.jpg', 'women/pashminas/animal-print-pashminas/cashmere-wrap-in-tiger-print', 'Tiger'),
  p('w_ps_006', 'F', 'Accessoires', "Cashmere Wrap with Fur Pompoms", 349, 699, '2023/07/8090a_resized-scaled-300x450.webp', 'women/pashminas/fur-and-fur-pashminas/cashmere-wrap-with-fur-pompoms', 'Noir'),
  p('w_ps_007', 'F', 'Accessoires', "Leopard Print Cashmere Scarf", 59, 119, '2023/06/8053b-scaled-300x450.jpg', 'women/pashminas/animal-print-pashminas/leopard-print-pashmina-shawl', 'Leopard'),
  p('w_ps_008', 'F', 'Accessoires', "Leopard Print Cashmere Shawl", 99, 199, '2023/06/6053a-scaled-300x450.jpg', 'women/pashminas/animal-print-pashminas/leopard-print-cashmere-shawl', 'Leopard'),
  p('w_ps_009', 'F', 'Accessoires', "Reversible Pashmina Wrap Purple & Gold", 79, 199, '2021/12/2107_resized-300x360.webp', 'women/pashminas/silk-cashmere-pashminas/two-tone-reversible-pashmina-wrap-in-purple-and-gold', 'Purple'),
  p('w_ps_010', 'F', 'Accessoires', "Cashmere Wrap Swarovski Diamond Border", 179, 259, '2023/11/Diamond-Border_a-scaled-300x450.webp', 'women/pashminas/swarovski-pashminas/cashmere-wrap-with-swarovski-beadwork-in-a-diamond-border', 'Noir'),
  p('w_ps_011', 'F', 'Accessoires', "Pashmina Shawl Swarovski Galaxy", 139, 279, '2023/07/6061_a_resized-scaled-300x450.webp', 'women/pashminas/swarovski-pashminas/pashmina-shawl-with-swarovski-crystals-galaxy', 'Galaxy'),
  p('w_ps_012', 'F', 'Accessoires', "Pashmina Beaded Tassels Magenta", 139, 279, '2023/06/6075b-scaled-300x450.jpg', 'women/pashminas/swarovski-pashminas/pashmina-shawl-with-beaded-tassels-magenta-pink', 'Magenta Pink'),
  p('w_ps_013', 'F', 'Accessoires', "Reversible Pashmina Blue & Purple", 79, 199, '2023/06/2112a-scaled-300x450.jpg', 'women/pashminas/silk-cashmere-pashminas/two-tone-reversible-pashmina-wrap-in-blue-and-purple', 'Blue Purple'),
  p('w_ps_014', 'F', 'Accessoires', "Reversible Pashmina Orange", 79, 199, '2023/06/2105a-scaled-300x450.jpg', 'women/pashminas/silk-cashmere-pashminas/two-tone-reversible-pashmina-wrap-in-orange', 'Orange'),
  p('w_ps_015', 'F', 'Accessoires', "Cashmere Wrap Zebra Print", 89, 179, '2023/06/7051a-scaled-300x450.jpg', 'women/pashminas/animal-print-pashminas/cashmere-wrap-in-animal-prints-zebra', 'Zebra'),
  p('w_ps_016', 'F', 'Accessoires', "Pashmina Beaded Tassels Vanilla", 139, 279, '2021/12/6076_resized-300x360.webp', 'women/pashminas/swarovski-pashminas/pashmina-shawl-with-beaded-tassels-vanilla', 'Vanilla'),
  p('w_ps_017', 'F', 'Accessoires', "Tiger Print Cashmere Shawl", 89, 199, '2023/06/6052a-scaled-300x450.jpg', 'women/pashminas/animal-print-pashminas/tiger-print-cashmere-shawl', 'Tiger'),
  p('w_ps_018', 'F', 'Accessoires', "Reversible Pashmina Blue", 79, 199, '2021/12/2116_resized-300x360.webp', 'women/pashminas/silk-cashmere-pashminas/two-tone-reversible-pashmina-wrap-in-blue', 'Blue'),
  p('w_ps_019', 'F', 'Accessoires', "Tiger Print Cashmere Scarf", 59, 119, '2023/06/8052a-scaled-300x450.jpg', 'women/pashminas/animal-print-pashminas/tiger-print-pashmina-scarf', 'Tiger'),

  // ─── MEN'S SWEATERS (12 produits) ───
  p('m_sw_001', 'H', 'Hauts', "Men's Pure Cashmere Polo Sweater", 149, 299, '2023/11/CSM9108_a-scaled-300x450.webp', 'gift-ideas/gifts-below-150/mens-pure-cashmere-polo-sweater', 'Noir'),
  p('m_sw_002', '*', 'Hauts', "Pure Cashmere Unisex Hoodie", 229, 459, '2023/11/CSM9110_f-scaled-300x450.webp', 'men/sweaters-men/mens-pure-cashmere-hoodie', 'Noir'),
  p('m_sw_003', '*', 'Ensembles', "Unisex Trouser & Sweater Lounge Set", 329, 659, '2023/11/CTSU_c-scaled-300x450.webp', 'men/robes-men/unisex-trouser-sweater-set-in-pure-cashmere', 'Noir'),
  p('m_sw_004', 'H', 'Hauts', "Men's V-Neck Cashmere Sweater", 139, 279, '2023/07/csm9105a_resized-scaled-300x450.webp', 'gift-ideas/gifts-below-150/mens-v-neck-cashmere-sweater', 'Noir'),
  p('m_sw_005', 'H', 'Hauts', "Men's Cashmere Cardigan", 149, 299, '2023/11/CSM9104_g-scaled-300x450.webp', 'gift-ideas/corporate-gift-ideas/mens-cashmere-cardigan', 'Noir'),
  p('m_sw_006', 'H', 'Hauts', "Men's Crew Neck Cashmere Sweater", 139, 279, '2023/11/CSM9101_a-scaled-300x450.webp', 'men/sweaters-men/mens-crew-neck-cashmere-sweater', 'Faded Pewter'),
  p('m_sw_007', 'H', 'Hauts', "Men's Sleeveless Cashmere Cardigan Vest", 129, 259, '2023/11/CSM9122_a-scaled-300x450.webp', 'men/sweaters-men/mens-cashmere-sleeveless-cardigan-vest', 'Melange Brown'),
  p('m_sw_008', 'H', 'Hauts', "Men's Two Tone Polo Sweater", 149, 299, '2023/11/CSM9107_a-scaled-300x450.webp', 'men/sweaters-men/mens-two-tone-polo-sweater-in-pure-cashmere', 'Navy'),
  p('m_sw_009', 'H', 'Hauts', "Men's Cable Cashmere Sweater", 169, 339, '2023/11/CSM9111_a-scaled-300x450.webp', 'gift-ideas/gifts-below-150/mens-cable-cashmere-sweater', 'Camel'),
  p('m_sw_010', 'H', 'Hauts', "Men's Cashmere Vest", 109, 219, '2023/11/CSM9121_a-scaled-300x450.webp', 'gift-ideas/gifts-below-100/mens-cashmere-vest', 'Faded Pewter'),
  p('m_sw_011', 'H', 'Hauts', "Men's Zip Vest in Pure Cashmere", 129, 259, '2023/11/CSM9123_a-scaled-300x450.webp', 'men/sweaters-men/mens-zip-vest-in-pure-cashmere', 'Charcoal'),
  p('m_sw_012', 'H', 'Hauts', "Pure Cashmere V-Neck Spring Sweater Men", 99, 199, '2023/11/CSM9109_a-scaled-300x450.webp', 'men/sweaters-men/pure-cashmere-v-neck-spring-sweater-for-men', 'Navy'),

  // ─── MEN'S ROBES (2 produits) ───
  p('m_rb_001', 'H', 'Robes', "Pure Cashmere Full Length Robe Men", 399, 799, '2023/11/CMR_a-scaled-300x450.webp', 'men/robes-men/pure-cashmere-full-length-robe-for-men', 'Noir'),
  p('m_rb_002', 'H', 'Robes', "Pure Cashmere Knee Length Robe Men", 379, 759, '2023/11/CMRS_d-scaled-300x450.webp', 'men/robes-men/pure-cashmere-knee-length-robe-for-men', 'Charcoal'),

  // ─── MEN'S COATS (7 produits) ───
  p('m_co_001', 'H', 'Vestes', "Men's Full Length Topcoat", 849, 1699, '2025/04/MOC80b-300x454.webp', 'men/coats-men/mens-full-length-topcoat-in-pure-cashmere', 'Noir'),
  p('m_co_002', 'H', 'Vestes', "Men's Knee Length Topcoat", 749, 1499, '2023/07/moc70a_resized-scaled-300x450.webp', 'men/coats-men/mens-knee-length-overcoat-in-pure-cashmere', 'Noir'),
  p('m_co_003', 'H', 'Vestes', "Men's Double Breasted Topcoat", 799, 1599, '2023/11/MOC90_c-scaled-300x450.webp', 'men/coats-men/mens-double-breasted-overcoat-in-pure-cashmere', 'Noir'),
  p('m_co_004', 'H', 'Vestes', "Cashmere Sport Coat for Men", 349, 699, '2023/11/MOC60_f-scaled-300x450.webp', 'men/coats-men/cashmere-sport-coat-for-men', 'Noir'),
  p('m_co_005', 'H', 'Vestes', "Men's Cashmere Pea Coat", 399, 799, '2023/11/MOBPC_a-scaled-300x450.webp', 'men/coats-men/mens-cashmere-pea-coat', 'Noir'),
  p('m_co_006', 'H', 'Vestes', "Men's Pure Cashmere Pea Coat", 599, 1199, '2021/12/MPC5-1-300x451.webp', 'men/coats-men/mens-pure-cashmere-pea-coat', 'Charcoal'),
  p('m_co_007', 'H', 'Vestes', "Men's Cashmere Jacket", 349, 699, '2023/11/MOBJK_a-scaled-300x450.webp', 'men/coats-men/mens-cashmere-jacket', 'Camel'),

  // ─── SCARVES (2 produits) ───
  p('s_001', '*', 'Accessoires', "Pure Cashmere Unisex Scarf", 69, 139, '2023/11/5200_h-scaled-300x450.webp', 'gift-ideas/corporate-gift-ideas/pure-cashmere-unisex-scarf', 'Noir', ['Unique'], '🧣'),
  p('s_002', '*', 'Accessoires', "Cashmere and Silk Scarf", 59, 119, '2023/11/5000_g-scaled-300x450.webp', 'women/pashminas/silk-cashmere-pashminas/pashmina-and-silk-scarf', 'Noir', ['Unique'], '🧣'),

  // ─── ACCESSORIES (11 produits) ───
  p('a_001', '*', 'Accessoires', "Pure Cashmere Unisex Socks", 59, 119, '2023/06/cs-scaled-300x450.jpg', 'gift-ideas/corporate-gift-ideas/pure-cashmere-socks', 'Noir', ['S', 'M', 'L'], '🧦'),
  p('a_002', '*', 'Accessoires', "Pure Cashmere Leg Warmers", 50, 119, '2023/11/LW_b-scaled-300x450.webp', 'gift-ideas/corporate-gift-ideas/pure-cashmere-leg-warmers', 'Noir', ['Unique'], '🧦'),
  p('a_003', '*', 'Accessoires', "Cashmere Scarf, Hat and Gloves Set", 129, 259, '2023/11/CSHG_b-scaled-300x450.webp', 'gift-ideas/gifts-below-150/unisex-pure-cashmere-scarf-hat-and-gloves-set', 'Noir', ['Unique'], '🧣'),
  p('a_004', '*', 'Accessoires', "Pure Cashmere Unisex Toque Hat", 39, 79, '2023/11/WCH_a-scaled-300x450.webp', 'gift-ideas/gifts-below-50/pure-cashmere-unisex-toque-hat', 'Noir', ['Unique'], '🎩'),
  p('a_005', '*', 'Accessoires', "Pure Cashmere Checks Scarf", 79, 139, '2023/07/5400a_resized-scaled-300x450.webp', 'accessories/pure-cashmere-scarves-in-checks', 'Checks', ['Unique'], '🧣'),
  p('a_006', '*', 'Accessoires', "Pure Cashmere Unisex Gloves", 39, 79, '2021/12/CG1_b_resized-300x360.webp', 'accessories/pure-cashmere-unisex-gloves-2', 'Noir', ['Unique'], '🧤'),
  p('a_007', '*', 'Accessoires', "Pure Cashmere Arm Warmers", 49, 99, '2023/11/CG2_b-scaled-300x450.webp', 'accessories/pure-cashmere-half-mittens', 'Noir', ['Unique'], '🧤'),
  p('a_008', '*', 'Accessoires', "Pure Cashmere Reversible Toque", 49, 99, '2023/11/CH1_c-scaled-300x450.webp', 'accessories/hats/pure-cashmere-reversible-toque-hat', 'Charcoal', ['Unique'], '🎩'),
  p('a_009', '*', 'Accessoires', "Pure Cashmere Ribbed Toque", 49, 99, '2023/11/CH2_a-scaled-300x450.webp', 'accessories/hats/pure-cashmere-ribbed-toque-hat', 'Camel', ['Unique'], '🎩'),
  p('a_010', '*', 'Accessoires', "Pure Cashmere Snood", 69, 139, '2023/07/CSD_resized-scaled-300x450.webp', 'accessories/hats/pure-cashmere-snood', 'Charcoal', ['Unique'], '🧣'),
  p('a_011', '*', 'Accessoires', "Pure Cashmere Wristlet", 49, 99, '2023/11/CWT_a-scaled-300x450.webp', 'accessories/pure-cashmere-wristlet', 'Charcoal', ['Unique'], '👛'),
];

console.log(`[CashmereService] ${ALL_PRODUCTS.length} produits actifs`);
